// eslint-disable-next-line no-restricted-imports -- firebase/authをimportするため
import {
  Auth,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  OAuthProvider,
  signInWithEmailAndPassword,
  signInWithPopup,
  UserCredential,
  connectAuthEmulator,
  verifyPasswordResetCode,
} from 'firebase/auth'
import { firebaseApp } from './app'
import { eventPush, setUserId } from '~/lib/gtmHelper'
import { removePfToken } from '~/features/provider/AppApolloProvider'
import { revokeSatisfactionSurvey } from '~/components/common/Enquate/SatisfactionSurvey'

// eslint-disable-next-line no-restricted-imports -- firebase/authをimportするため
export type { User as FirebaseUser } from 'firebase/auth'

const firebaseAuth = getAuth(firebaseApp)

// 環境変数でFirebase Auth Emulatorに接続
if (typeof window !== 'undefined' && process.env.NEXT_PUBLIC_USE_FIREBASE_EMULATOR === 'true') {
  try {
    // @ts-expect-error -- canInitEmulatorは内部プロパティ
    if (firebaseAuth._canInitEmulator !== false) {
      connectAuthEmulator(firebaseAuth, 'http://localhost:9099', {
        disableWarnings: true,
      })
    }
  } catch {
    // 既に接続されている場合はエラーが出るが無視
  }
}

const fixed = <Args extends unknown[], Result>(fn: (auth: Auth, ...args: Args) => Result) => {
  return (...args: Args) => fn(firebaseAuth, ...args)
}

export const auth = {
  onAuthStateChanged: firebaseAuth.onAuthStateChanged.bind(firebaseAuth),

  signInWithEmailAndPassword: fixed(signInWithEmailAndPassword),
  signUpWithUserWithEmailAndPassword: fixed(createUserWithEmailAndPassword),
  fetchSignInMethodsForEmail: fixed(fetchSignInMethodsForEmail),
  verifyPasswordResetCode: fixed(verifyPasswordResetCode),
  sendPasswordResetEmail: fixed(sendPasswordResetEmail),

  getCurrentIdToken: () => firebaseAuth.currentUser?.getIdToken(),

  signOut: async () => {
    await firebaseAuth.signOut()
    removePfToken()
    revokeSatisfactionSurvey()
    setUserId(null)
    eventPush('sign_out', {})
  },
  // firebase signoutを除いたsignout処理
  // signoutを呼んでしまうと、AuthContext内の処理が走ってしまって、/ にリダイレクトされてしまうため
  signOutWithCleanup: async () => {
    removePfToken()
    revokeSatisfactionSurvey()
    setUserId(null)
  },

  signInWithGoogle: async () => {
    const provider = new GoogleAuthProvider()
    const cred = await signInWithPopup(firebaseAuth, provider)
    sendNewUserEvent(cred, 'google')
    eventPush('login', { method: 'google' })
  },

  signInWithApple: async () => {
    const provider = new OAuthProvider('apple.com')
    provider.setCustomParameters({ locale: 'ja' })
    const cred = await signInWithPopup(firebaseAuth, provider)
    sendNewUserEvent(cred, 'apple')
    eventPush('login', { method: 'apple' })
  },
}

const sendNewUserEvent = (cred: UserCredential, provider: 'apple' | 'google') => {
  const additionalUserInfo = getAdditionalUserInfo(cred)
  if (additionalUserInfo?.isNewUser) {
    eventPush('create_firebase_user', {
      method: provider,
    })
  }
}
